"use client"

import { useEffect } from "react"

/**
 * Global client-error reporter. Catches uncaught JS errors and unhandled
 * promise rejections, POSTs them to /api/errors, which writes them to the
 * frontend container's stdout so they're visible in prod logs.
 *
 * Mounted once from the root layout. No visible UI.
 */
export function ErrorReporter() {
  useEffect(() => {
    function send(event: Record<string, unknown>) {
      const body = JSON.stringify({
        ...event,
        url: window.location.href,
        ts: new Date().toISOString(),
      })
      // keepalive so the request still flies on page unload
      fetch("/api/errors", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        keepalive: true,
      }).catch(() => { /* swallow — logging must never break the page */ })
    }

    const onError = (e: ErrorEvent) => {
      send({
        type: "error",
        message: e.message,
        filename: e.filename,
        lineno: e.lineno,
        colno: e.colno,
        stack: e.error instanceof Error ? e.error.stack : undefined,
      })
    }

    const onRejection = (e: PromiseRejectionEvent) => {
      const reason = e.reason
      send({
        type: "unhandledrejection",
        message: reason instanceof Error ? reason.message : String(reason),
        stack: reason instanceof Error ? reason.stack : undefined,
      })
    }

    window.addEventListener("error", onError)
    window.addEventListener("unhandledrejection", onRejection)
    return () => {
      window.removeEventListener("error", onError)
      window.removeEventListener("unhandledrejection", onRejection)
    }
  }, [])

  return null
}
